import { useState } from "react";
import { Copy, Plus, Trash2 } from "lucide-react";
import type { ZETenderVariant } from "../types";

interface Props {
  variants: ZETenderVariant[];
  activeVariantId: string | null;
  dirty: boolean;
  onSelect: (id: string) => void;
  onCreate: (label: string) => void;
  onDelete: (id: string) => void;
  onCopyFrom?: (sourceId: string, label: string) => void;
  copyPending?: boolean;
  /** In fullscreen the delete-variant trash is hidden (the page's top-right
   *  corner is taken over by the "Zamknij pełny ekran" button instead). */
  isFullscreen?: boolean;
}

const guardSwitch = (dirty: boolean) =>
  !dirty || window.confirm("Masz niezapisane zmiany w rysunku. Przełączyć wariant i porzucić zmiany?");

/** Independent route variants (A/B/C). Switching reloads that variant's drawing, summary
 *  and parcels. Each variant is fully independent. */
export function ZEVariantSwitcher({ variants, activeVariantId, dirty, onSelect, onCreate, onDelete, onCopyFrom, copyPending, isFullscreen }: Props) {
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [copyPicker, setCopyPicker] = useState(false);

  const nextLabel = () => {
    const used = new Set(variants.map((v) => v.label.toUpperCase()));
    for (let c = 65; c <= 90; c++) if (!used.has(String.fromCharCode(c))) return String.fromCharCode(c);
    return "";
  };

  const submit = () => {
    const l = (label || nextLabel()).trim();
    if (!l) return;
    onCreate(l);
    setLabel("");
    setAdding(false);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex border border-[var(--color-border)]">
        {variants.length === 0 && (
          <span className="px-3 py-1.5 text-[11px] font-mono text-[var(--color-text-muted)]">— brak wariantów —</span>
        )}
        {variants.map((v) => {
          const active = v.id === activeVariantId;
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => active || (guardSwitch(dirty) && onSelect(v.id))}
              className={`px-3 py-1.5 font-mono text-sm font-bold border-r border-[var(--color-border)] last:border-r-0 transition-colors ${
                active
                  ? "bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
                  : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
              }`}
              title={`Wariant ${v.label}${v.parcels_recomputed_at ? " · działki przeliczone" : ""}`}
            >
              {v.label}
            </button>
          );
        })}
      </div>

      {adding ? (
        <div className="flex items-center gap-1">
          <input
            autoFocus
            className="input-base text-xs py-1 w-16"
            placeholder={nextLabel() || "A"}
            value={label}
            onChange={(e) => setLabel(e.target.value.toUpperCase().slice(0, 8))}
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
          <button type="button" className="text-[11px] font-mono text-[var(--color-primary)]" onClick={submit}>
            dodaj
          </button>
          <button type="button" className="text-[11px] font-mono text-[var(--color-text-muted)]" onClick={() => setAdding(false)}>
            anuluj
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 px-2 py-1.5 text-[11px] font-mono text-[var(--color-text-secondary)] border border-dashed border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)] transition-colors"
          >
            <Plus size={13} /> Nowy wariant
          </button>
          {onCopyFrom && variants.length > 0 && (
            <div className="relative">
              <button
                type="button"
                disabled={copyPending}
                onClick={() => {
                  if (variants.length === 1) {
                    onCopyFrom(variants[0].id, nextLabel());
                  } else {
                    setCopyPicker((o) => !o);
                  }
                }}
                className="inline-flex items-center gap-1 px-2 py-1.5 text-[11px] font-mono text-[var(--color-text-secondary)] border border-dashed border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)] transition-colors disabled:opacity-50"
                title="Nowy wariant na podstawie istniejącego"
              >
                <Copy size={12} /> Na podstawie istniejącego
              </button>
              {copyPicker && (
                <div className="absolute left-0 top-full mt-1 z-50 bg-white dark:bg-gray-800 border border-[var(--color-border)] rounded shadow-lg min-w-[160px]">
                  {variants.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => {
                        onCopyFrom(v.id, nextLabel());
                        setCopyPicker(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-[11px] font-mono hover:bg-[var(--color-bg-elevated)] text-left"
                    >
                      <span className="font-bold">{v.label}</span>
                      <span className="text-[var(--color-text-muted)] truncate">
                        {v.lines_count + v.objects_count} el.
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeVariantId && variants.length > 0 && !isFullscreen && (
        <button
          type="button"
          title="Usuń aktywny wariant"
          onClick={() => {
            if (window.confirm("Usunąć ten wariant wraz z rysunkiem i działkami?")) onDelete(activeVariantId);
          }}
          className="ml-auto p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
        >
          <Trash2 size={15} />
        </button>
      )}
    </div>
  );
}
